import { useState, type FormEvent } from 'react'
import {
  DeleteAccountError,
  deleteAccountFlow,
} from '@features/account/services/deleteAccountFlow'
import { HighlightEmails } from '@shared/ui/HighlightEmails'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { LOGIN_ROUTE } from '@constants/routes'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * Settings § account: アカウントの削除 (アカウント完全削除) 確認モーダル。
 *
 * UX 要件:
 *   - 二段ガード: (1) 自分の email を type させて入力一致確認 (2) master password 入力
 *   - 成功時は即 #login へ遷移 (確認モーダル不要 — Notice での `success` は表示せず、副作用で完結)
 *   - 失敗時は session を残してエラー Notice 表示 (再試行可能)
 *
 * セキュリティ要件 (CLAUDE.md §2):
 *   - master password / email 入力値は React state に短期保持し、submit 後 clear
 *   - 削除フロー本体は `deleteAccountFlow` に委譲 (zero-knowledge / sessionStore.clear 含む)
 */

type Phase =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; messageKey: string }

const ERROR_KEY: Record<DeleteAccountError['code'], string> = {
  no_session: 'settings.account.delete.error.no_session',
  wrong_password: 'settings.account.delete.error.wrong_password',
  too_many_requests: 'settings.account.delete.error.too_many_requests',
  unauthorized: 'settings.account.delete.error.unauthorized',
  network: 'settings.account.delete.error.network',
  generic: 'settings.account.delete.error.generic',
}

export interface DeleteAccountSectionProps {
  /** 削除予定のユーザー email (UI 一致確認 + warning 表示に使用)。 */
  email: string
  onClose: () => void
}

export const DeleteAccountSection = ({
  email,
  onClose,
}: DeleteAccountSectionProps): JSX.Element => {
  const { t } = useTranslation()
  const [emailConfirm, setEmailConfirm] = useState('')
  const [password, setPassword] = useState('')
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  const emailMatches = emailConfirm.trim().toLowerCase() === email.toLowerCase()
  const isSubmitting = phase.kind === 'submitting'
  const canSubmit = emailMatches && password.length > 0 && !isSubmitting

  // email エラー表示: 何か入力されたが一致しない場合のみ赤くする (空欄では出さない)
  const showEmailInvalid = emailConfirm.length > 0 && !emailMatches

  // password エラー表示: サーバが wrong_password を返した直後のみ赤くする
  // (空欄や未送信時に赤くしない)
  const showPasswordInvalid =
    phase.kind === 'error' && phase.messageKey === ERROR_KEY.wrong_password

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    if (!canSubmit) return
    setPhase({ kind: 'submitting' })
    try {
      await deleteAccountFlow(password)
      // 成功 — sessionStore は flow 内で clear 済。即 #login へ遷移。
      setPassword('')
      setEmailConfirm('')
      window.location.hash = LOGIN_ROUTE
    } catch (err: unknown) {
      const code: DeleteAccountError['code'] =
        err instanceof DeleteAccountError ? err.code : 'generic'

      setPassword('')
      setPhase({ kind: 'error', messageKey: ERROR_KEY[code] })
    }
  }

  return (
    <div
      className="vault-delete-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
      onClick={ev => {
        if (ev.target === ev.currentTarget && !isSubmitting) onClose()
      }}
    >
      <div className="vault-delete-dialog">
        <form onSubmit={ev => void onSubmit(ev)} data-testid="delete-account-form">
          <h3 id="delete-account-title">{t('settings.account.delete.title')}</h3>
          <Notice tone="danger" ico="alert">
            <HighlightEmails text={t('settings.account.delete.warning')} />
          </Notice>

          <ul className="delete-account-bullets">
            <li>{t('settings.account.delete.bullet_vault')}</li>
            <li>{t('settings.account.delete.bullet_sends')}</li>
            <li>{t('settings.account.delete.bullet_devices')}</li>
            <li>
              <HighlightEmails text={t('settings.account.delete.bullet_irreversible')} />
            </li>
            <li>{t('settings.account.delete.bullet_legal')}</li>
          </ul>

          <p className="delete-account-terms-link">
            <a
              href="#terms"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="delete-account-terms-link"
            >
              {t('settings.account.delete.terms_link')} →
            </a>
          </p>

          <div className="field">
            <label className="field-label" htmlFor="delete-account-email">
              {t('settings.account.delete.email_label', { email })}
            </label>
            <input
              id="delete-account-email"
              type="email"
              className={`input${showEmailInvalid ? ' input--invalid' : ''}`}
              value={emailConfirm}
              onChange={ev => {
                setEmailConfirm(ev.target.value)
                // 入力変更で過去の wrong_password 表示はリセットして良い体験を保つ
                if (phase.kind === 'error') setPhase({ kind: 'idle' })
              }}
              placeholder={email}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={showEmailInvalid}
              aria-describedby={showEmailInvalid ? 'delete-account-email-error' : undefined}
              required
              data-testid="delete-account-email-input"
            />
            {showEmailInvalid && (
              <p
                id="delete-account-email-error"
                className="field-error"
                data-testid="delete-account-email-error"
              >
                {t('settings.account.delete.email_mismatch')}
              </p>
            )}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="delete-account-password">
              {t('settings.account.delete.password_label')}
            </label>
            <PasswordInput
              id="delete-account-password"
              name="masterPassword"
              autoComplete="current-password"
              value={password}
              onChange={ev => {
                setPassword(ev.target.value)
                if (phase.kind === 'error') setPhase({ kind: 'idle' })
              }}
              className={showPasswordInvalid ? 'input--invalid' : ''}
              aria-invalid={showPasswordInvalid}
              required
            />
          </div>

          {phase.kind === 'error' && (
            <Notice tone="danger" ico="alert">
              {t(phase.messageKey)}
            </Notice>
          )}

          <div className="vault-delete-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('settings.account.delete.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-danger"
              disabled={!canSubmit}
              data-testid="delete-account-submit"
            >
              <Icon name="trash" aria-hidden="true" />
              {isSubmitting
                ? t('settings.account.delete.submitting')
                : t('settings.account.delete.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
